import React, { useMemo } from "react";
import { Modal, Spin } from "antd";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  ReferenceLine,
  Legend,
} from "recharts";
import {
  useRunStandaloneQuery,
  useGetTaskStandAloneByIdQuery,
} from "shared/api";
import dayjs from "dayjs";

interface ChartPoint {
  time: string;
  value: number;
}

interface Props {
  taskId: number | null;
  open: boolean;
  onClose: () => void;
}

const CustomTooltip = ({ active, payload, label, taskData }: any) => {
  if (!active || !payload?.length) return null;

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #ccc",
        padding: "10px",
        borderRadius: "6px",
      }}
    >
      <div>
        <strong>{dayjs(label).format("MM/DD/YYYY HH:mm")}</strong>
      </div>

      <div style={{ color: "#1677ff" }}>
        Value: {payload[0].value.toFixed(2)}
      </div>

      {taskData && (
        <>
          <div style={{ color: "red" }}>Min Hard: {taskData.min_hard}</div>
          <div style={{ color: "orange" }}>Min Soft: {taskData.min_soft}</div>
          <div style={{ color: "green" }}>Target: {taskData.target_value}</div>
          <div style={{ color: "orange" }}>Max Soft: {taskData.max_soft}</div>
          <div style={{ color: "red" }}>Max Hard: {taskData.max_hard}</div>
        </>
      )}
    </div>
  );
};

export const StandaloneTaskChart: React.FC<Props> = ({
  taskId,
  open,
  onClose,
}) => {
  const { data: runData, isLoading } = useRunStandaloneQuery(taskId!, {
    skip: !taskId,
  });

  const { data: taskData } = useGetTaskStandAloneByIdQuery(
    { id: taskId! },
    { skip: !taskId }
  );

  const chartData: ChartPoint[] = useMemo(() => {
    if (!runData?.result || !taskData?.field) return [];

    return runData.result
      .map((item: any) => {
        const raw = item[taskData.field];
        const value = Number(raw);

        if (!Number.isFinite(value)) return null;

        return {
          time: item.timestamp,
          value,
        };
      })
      .filter(Boolean) as ChartPoint[];
  }, [runData, taskData]);

  const yDomain: [number, number] | ["auto", "auto"] = useMemo(() => {
    if (!taskData || !chartData.length) {
      return ["auto", "auto"];
    }

    const values = chartData.map((d) => d.value);

    const minValue = Math.min(taskData.min_hard, ...values);
    const maxValue = Math.max(taskData.max_hard, ...values);

    const range = maxValue - minValue;

    const padding = range * 0.2;

    return [Math.floor(minValue - padding), Math.ceil(maxValue + padding)];
  }, [taskData, chartData]);

  return (
    <Modal open={open} onCancel={onClose} footer={null} width={1200} centered>
      {isLoading ? (
        <div className="flex items-center justify-center h-[400px]">
          <Spin size="large" />
        </div>
      ) : (
        <div className="flex items-center justify-center h-[500px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid stroke="#eee" strokeDasharray="5 5" />

              <XAxis
                dataKey="time"
                tickFormatter={(time) => dayjs(time).format("MM/DD HH:mm")}
              />

              <YAxis
                type="number"
                domain={yDomain}
                allowDecimals
                allowDataOverflow={false}
                tickFormatter={(v) => v.toFixed(1)}
              />

              <Tooltip content={<CustomTooltip taskData={taskData} />} />
              <Legend />

              <Line
                type="monotone"
                dataKey="value"
                stroke="#1677ff"
                strokeWidth={2}
                dot={{ r: 2 }}
                name="Value"
                isAnimationActive={false}
              />

              {taskData && (
                <>
                  <ReferenceLine
                    y={taskData.min_hard}
                    stroke="red"
                    strokeWidth={2}
                    strokeDasharray="6 4"
                    label="Min Hard"
                  />
                  <ReferenceLine
                    y={taskData.max_hard}
                    stroke="red"
                    strokeWidth={2}
                    strokeDasharray="6 4"
                    label="Max Hard"
                  />
                  <ReferenceLine
                    y={taskData.min_soft}
                    stroke="orange"
                    strokeWidth={2}
                    strokeDasharray="4 4"
                    label="Min Soft"
                  />
                  <ReferenceLine
                    y={taskData.max_soft}
                    stroke="orange"
                    strokeWidth={2}
                    strokeDasharray="4 4"
                    label="Max Soft"
                  />
                  <ReferenceLine
                    y={taskData.target_value}
                    stroke="green"
                    strokeWidth={3}
                    label="Target"
                  />
                </>
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </Modal>
  );
};
